import {
  getLeoUuid,
  LeoUUID,
  getObject,
  getLeoTimestamp,
  LeoTimestamp,
  getLeoDate,
  LeoDate,
  getBoolean,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { SpocUserDetails, SpocUserDetailsDTO } from "../types/spocUserDetails";
/* eslint-disable import/export */
export abstract class GetSpocDetailsRPC implements
  LeoRPC<GetSpocDetailsRPC.Request, GetSpocDetailsRPC.Response, GetSpocDetailsRPC.Errors.Errors>
{
  abstract execute(
    request: GetSpocDetailsRPC.Request
  ): Promise<LeoRPCResult<GetSpocDetailsRPC.Response, GetSpocDetailsRPC.Errors.Errors>>;
}

export namespace GetSpocDetailsRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    projectId: string;
    spocId: string;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isGetSpocDetailsRPCRequest: boolean = true;
  
    readonly projectId: LeoUUID;
    readonly spocId: LeoUUID;
  
  
    constructor(
    projectId: LeoUUID, 
    spocId: LeoUUID
    ) {
      this.projectId = projectId;
      this.spocId = spocId;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      const spocId = getLeoUuid(dto, "spocId", InvalidRequestSpocIdError);
      return new Request(
        projectId,
        spocId
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          projectId: this.projectId.toDTO(),
          spocId: this.spocId.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `GetSpocDetailsRPC(projectId='${this.projectId}',spocId='${this.spocId}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    spocDetails: SpocUserDetailsDTO;
    projectId: string;
    addedOn: string;
    expiresOn: string;
    isDownloadingSurveyReportAllowed: boolean;
    isViewingIndividualSurveyResponseAllowed: boolean;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isGetSpocDetailsResponse: boolean = true;
  
    readonly spocDetails: SpocUserDetails;
    readonly projectId: LeoUUID;
    readonly addedOn: LeoTimestamp;
    readonly expiresOn: LeoDate;
    readonly isDownloadingSurveyReportAllowed: boolean;
    readonly isViewingIndividualSurveyResponseAllowed: boolean;
  
  
    constructor(
    spocDetails: SpocUserDetails, 
    projectId: LeoUUID, 
    addedOn: LeoTimestamp, 
    expiresOn: LeoDate, 
    isDownloadingSurveyReportAllowed: boolean, 
    isViewingIndividualSurveyResponseAllowed: boolean
    ) {
      this.spocDetails = spocDetails;
      this.projectId = projectId;
      this.addedOn = addedOn;
      this.expiresOn = expiresOn;
      this.isDownloadingSurveyReportAllowed = isDownloadingSurveyReportAllowed;
      this.isViewingIndividualSurveyResponseAllowed = isViewingIndividualSurveyResponseAllowed;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const spocDetails = SpocUserDetails.fromDTO(getObject(dto, "spocDetails", InvalidResponseSpocDetailsError));
      const projectId = getLeoUuid(dto, "projectId", InvalidResponseProjectIdError);
      const addedOn = getLeoTimestamp(dto, "addedOn", InvalidResponseAddedOnError);
      const expiresOn = getLeoDate(dto, "expiresOn", InvalidResponseExpiresOnError);
      const isDownloadingSurveyReportAllowed = getBoolean(dto, "isDownloadingSurveyReportAllowed", InvalidResponseIsDownloadingSurveyReportAllowedError);
      const isViewingIndividualSurveyResponseAllowed = getBoolean(dto, "isViewingIndividualSurveyResponseAllowed", InvalidResponseIsViewingIndividualSurveyResponseAllowedError);
      return new Response(
        spocDetails,
        projectId,
        addedOn,
        expiresOn,
        isDownloadingSurveyReportAllowed,
        isViewingIndividualSurveyResponseAllowed
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          spocDetails: this.spocDetails.toDTO(),
          projectId: this.projectId.toDTO(),
          addedOn: this.addedOn.toDTO(),
          expiresOn: this.expiresOn.toDTO(),
          isDownloadingSurveyReportAllowed: this.isDownloadingSurveyReportAllowed,
          isViewingIndividualSurveyResponseAllowed: this.isViewingIndividualSurveyResponseAllowed
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `GetSpocDetails(spocDetails='${this.spocDetails}',projectId='${this.projectId}',addedOn='${this.addedOn}',expiresOn='${this.expiresOn}',isDownloadingSurveyReportAllowed='${this.isDownloadingSurveyReportAllowed}',isViewingIndividualSurveyResponseAllowed='${this.isViewingIndividualSurveyResponseAllowed}')`;
  };
  

  export enum RPCError {
    InvalidProjectId = "INVALID_PROJECT_ID",
    InvalidSpocId = "INVALID_SPOC_ID"
  }

  export namespace Errors {
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `GetSpocDetailsRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface InvalidSpocIdDTO {
      code: string;
    }
    
    export class InvalidSpocId extends LeoRPCError<RPCError> {
      readonly isInvalidSpocId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSpocId;
        super(code);
      }
    
    
      toDTO(): InvalidSpocIdDTO {
        const dto: InvalidSpocIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSpocId.prototype.toString = function toString(): string {
      return `GetSpocDetailsRPC.INVALID_SPOC_ID()`;
    };
    

    export type Errors = InvalidProjectId | InvalidSpocId;
    export type ErrorsDTO = InvalidProjectIdDTO | InvalidSpocIdDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.InvalidSpocId:
          return new InvalidSpocId();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestProjectIdError extends GetSpocDetailsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidRequestSpocIdError extends GetSpocDetailsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSpocIdError";
  }
}

export class InvalidResponseSpocDetailsError extends GetSpocDetailsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSpocDetailsError";
  }
}

export class InvalidResponseProjectIdError extends GetSpocDetailsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectIdError";
  }
}

export class InvalidResponseAddedOnError extends GetSpocDetailsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseAddedOnError";
  }
}

export class InvalidResponseExpiresOnError extends GetSpocDetailsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseExpiresOnError";
  }
}

export class InvalidResponseIsDownloadingSurveyReportAllowedError extends GetSpocDetailsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseIsDownloadingSurveyReportAllowedError";
  }
}

export class InvalidResponseIsViewingIndividualSurveyResponseAllowedError extends GetSpocDetailsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseIsViewingIndividualSurveyResponseAllowedError";
  }
}

export class InvalidResponseInvalidProjectIdError extends GetSpocDetailsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseInvalidSpocIdError extends GetSpocDetailsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSpocIdError";
  }
}

